"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isValidCents = exports.parseCurrencyToCents = exports.formatCentsToDecimal = exports.formatMoney = exports.formatCents = exports.centsToDollars = exports.dollarsToCents = exports.MoneyService = void 0;
const primitives_1 = require("../../primitives");
/**
 * Centralized Money Service for zlinkx
 *
 * This service provides consistent currency handling using cents (integers).
 * All monetary values are stored and transmitted as cents to avoid floating-point issues.
 *
 * Example: $99.99 = 9999 cents
 */
class MoneyService {
    /**
     * Conversion factor from dollars to cents
     */
    static CENTS_PER_DOLLAR = primitives_1.CENTS_PER_DOLLAR;
    /**
     * Convert dollars to cents
     * @param dollars - Amount in dollars (e.g., 99.99)
     * @returns Amount in cents as integer (e.g., 9999)
     */
    static dollarsToCents(dollars) {
        if (typeof dollars !== 'number' || isNaN(dollars)) {
            return 0;
        }
        return Math.round(dollars * this.CENTS_PER_DOLLAR);
    }
    /**
     * Convert cents to dollars
     * @param cents - Amount in cents (e.g., 9999)
     * @returns Amount in dollars (e.g., 99.99)
     */
    static centsToDollars(cents) {
        if (typeof cents !== 'number' || isNaN(cents)) {
            return 0;
        }
        return cents / this.CENTS_PER_DOLLAR;
    }
    /**
     * Format cents as a localized currency string
     * @param cents - Amount in cents (e.g., 9999)
     * @param locale - Locale for formatting (default: 'en-US')
     * @param currency - Currency code (default: 'USD')
     * @returns Formatted currency string (e.g., "$99.99")
     */
    static formatCents(cents, locale = 'en-US', currency = 'USD') {
        if (typeof cents !== 'number' || isNaN(cents)) {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: currency,
            }).format(0);
        }
        const dollars = this.centsToDollars(cents);
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
        }).format(dollars);
    }
    /**
     * Simple format for cents to dollar string
     * @param cents - Amount in cents
     * @returns Simple formatted string (e.g., "$99.99")
     */
    static formatMoney(cents) {
        if (typeof cents !== 'number' || isNaN(cents)) {
            return '$0.00';
        }
        return `$${(cents / this.CENTS_PER_DOLLAR).toFixed(2)}`;
    }
    /**
     * Format cents to a display string without currency symbol
     * @param cents - Amount in cents
     * @returns Formatted string (e.g., "99.99")
     */
    static formatCentsToDecimal(cents) {
        if (typeof cents !== 'number' || isNaN(cents)) {
            return '0.00';
        }
        return (cents / this.CENTS_PER_DOLLAR).toFixed(2);
    }
    /**
     * Parse a currency input string to cents
     * Handles various formats: "100", "100.00", "$100.00", "$ 100.00", "1,000.00"
     * @param input - Currency input string
     * @returns Amount in cents, or NaN if invalid
     */
    static parseCurrencyToCents(input) {
        if (typeof input !== 'string') {
            return NaN;
        }
        // Remove currency symbols, commas, and spaces
        const cleaned = input.replace(/[$,\s]/g, '');
        const dollars = parseFloat(cleaned);
        if (isNaN(dollars)) {
            return NaN;
        }
        return this.dollarsToCents(dollars);
    }
    /**
     * Validate if a value is a valid monetary amount in cents
     * @param cents - Value to validate
     * @returns True if valid (non-negative integer)
     */
    static isValidCents(cents) {
        return typeof cents === 'number' && Number.isInteger(cents) && cents >= 0;
    }
    /**
     * Add two monetary amounts in cents
     * @param a - First amount in cents
     * @param b - Second amount in cents
     * @returns Sum in cents
     */
    static add(a, b) {
        return (a || 0) + (b || 0);
    }
    /**
     * Subtract monetary amounts in cents
     * @param a - Amount to subtract from (in cents)
     * @param b - Amount to subtract (in cents)
     * @returns Difference in cents
     */
    static subtract(a, b) {
        return (a || 0) - (b || 0);
    }
    /**
     * Multiply amount by a factor (useful for quantity calculations)
     * @param cents - Amount in cents
     * @param factor - Multiplier
     * @returns Result in cents (rounded to integer)
     */
    static multiply(cents, factor) {
        return Math.round((cents || 0) * factor);
    }
    /**
     * Calculate percentage of an amount
     * @param cents - Base amount in cents
     * @param percentage - Percentage (e.g., 15 for 15%)
     * @returns Calculated amount in cents (rounded)
     */
    static percentage(cents, percentage) {
        return Math.round((cents * percentage) / 100);
    }
    /**
     * Compare two monetary amounts
     * @param a - First amount in cents
     * @param b - Second amount in cents
     * @returns -1 if a < b, 0 if equal, 1 if a > b
     */
    static compare(a, b) {
        if (a < b)
            return -1;
        if (a > b)
            return 1;
        return 0;
    }
    /**
     * Get the minimum of multiple amounts
     * @param amounts - Array of amounts in cents
     * @returns Minimum amount
     */
    static min(...amounts) {
        return Math.min(...amounts.filter(a => typeof a === 'number' && !isNaN(a)));
    }
    /**
     * Get the maximum of multiple amounts
     * @param amounts - Array of amounts in cents
     * @returns Maximum amount
     */
    static max(...amounts) {
        return Math.max(...amounts.filter(a => typeof a === 'number' && !isNaN(a)));
    }
}
exports.MoneyService = MoneyService;
// Export utility functions for direct use
exports.dollarsToCents = MoneyService.dollarsToCents.bind(MoneyService);
exports.centsToDollars = MoneyService.centsToDollars.bind(MoneyService);
exports.formatCents = MoneyService.formatCents.bind(MoneyService);
exports.formatMoney = MoneyService.formatMoney.bind(MoneyService);
exports.formatCentsToDecimal = MoneyService.formatCentsToDecimal.bind(MoneyService);
exports.parseCurrencyToCents = MoneyService.parseCurrencyToCents.bind(MoneyService);
exports.isValidCents = MoneyService.isValidCents.bind(MoneyService);
